import type { IAMPlatformLoginResponse } from './types'
import { StateType } from '@wix/ambassador-iam-authentication-v1-authentication-http/types'

export type IPrivacyStatus = 'PUBLIC' | 'PRIVATE'

/*
 * ── Why this exists (read before touching the social-login response handling) ──
 *
 * The social login response is typed as `IAMPlatformLoginResponse` (= the flat `StateMachineResponse`,
 * where `state` is a `StateType` string). THAT TYPE IS A LIE for social login. The SSO endpoint the
 * popup calls — `GET /_api/iam/authentication/v1/sso/login/{metaSiteId}/{idpConnectionId}`
 * (`LoginWithIdpConnection`) — returns a `RawHttpResponse` (an untyped body), and the body it actually
 * carries is the LEGACY `LoginResponse` proto, where the state is NESTED:
 *
 *     { state: { status: <StateStatus>, stateToken }, sessionToken, identity, additionalData }   ← actual (nested)
 *     { state: <StateType>, stateToken, sessionToken, identity, additionalData }                 ← what the type claims (flat)
 *
 * Because the endpoint is raw/untyped, nothing generates a type for the real body, so the code just
 * asserts `StateMachineResponse`. The two shapes OVERLAP on `sessionToken` / `identity` / `additionalData`
 * (same place in both), which is why a *successful* social login always worked and the mismatch stayed
 * dormant for years — the only fields read were the overlapping ones. It only became a bug once code
 * started reading `loginResponse.state` for the parked states (REQUIRE_MISSING_FIELDS /
 * REQUIRE_OWNER_APPROVAL): on the nested body, `.state` is an object, so `=== StateType.X` is always false,
 * the parked branch is skipped, and the flow falls through to `applySessionToken('')` → 400 → generic error,
 * and no popup. Owner-approval regressed the same way (it used to read `identity.status.name`).
 *
 * Why normalize to the FLAT shape (instead of reading `state.status` everywhere): the SAME handler also
 * handles the `completeProfile` response, which IS a real flat `StateMachineResponse`, and both flow
 * through the same owner-approval + session-issuing tail. Converting the nested SSO body to flat up front
 * means that shared code reads ONE shape, with no per-shape branching.
 *
 * Both social entry points (`loginWithIdp` here, and `onBackendSocialLogin` from the editor-elements
 * MemberLoginDialog/SignUpDialog widget) call this same SSO endpoint, so the nested body is the ONE shape
 * social login produces — normalize it once, at `handleSocialLoginResponse` (their convergence point).
 *
 * Long-term fix (not done here): give the SSO endpoint a typed response (its sibling
 * `LoginWithIdpConnectionTokenParams` already returns a real `StateMachineResponse`), which removes the
 * raw/untyped body this whole workaround exists for.
 */
type SsoLoginState = { status?: string; stateToken?: string | null }
export type SsoLoginResponse = Omit<IAMPlatformLoginResponse, 'state'> & { state?: SsoLoginState | StateType }

// Convert the nested legacy SSO `LoginResponse` into the flat `StateMachineResponse` the handlers consume:
// take the real state from `state.status` (only `DONE` differs from its StateType counterpart `SUCCESS`;
// every other value — REQUIRE_MISSING_FIELDS / REQUIRE_OWNER_APPROVAL / STATUS_CHECK / REQUIRE_FIRST_FACTOR —
// is an identical string, so it passes through) and lift `state.stateToken` to the top level. This mirrors
// the backend's own `AuthenticationMappers.toStateType` (StateStatus → StateType) — see identity #5116.
// A response that is ALREADY flat (state is a string, or absent) is returned unchanged, so this is safe to
// run on any social response and never corrupts a shape it doesn't recognize.
export const normalizeSocialLoginResponse = (payload: IAMPlatformLoginResponse): IAMPlatformLoginResponse => {
	const state = (payload as SsoLoginResponse).state
	if (!state || typeof state !== 'object') {
		return payload
	}
	return {
		...payload,
		state: (state.status === 'DONE' ? StateType.SUCCESS : state.status) as StateType,
		stateToken: state.stateToken ?? payload.stateToken,
	}
}

export const openIdpPopup = async (
	idpConnectionId: string,
	biVisitorId: string,
	bsi: string,
	svSession: string,
	metaSiteId: string,
	isCommunityChecked: boolean
): Promise<IAMPlatformLoginResponse> => {
	const searchParams = new URLSearchParams({
		visitorId: biVisitorId,
		bsi,
		svSession,
		privacyStatus: getPrivacyStatus(isCommunityChecked),
		tenantType: 'SITE',
	})

	const baseUrl = `/_api/iam/authentication/v1/sso/login/${metaSiteId}/${idpConnectionId}`
	const url = addParamsToUrl(new URL(baseUrl, window.location.origin), searchParams)

	return await openPopUrl(url)
}

const openPopUrl = (url: string): Promise<IAMPlatformLoginResponse> => {
	return new Promise(async (resolve, reject) => {
		const sessionId = window.crypto.randomUUID()
		const key = await window.crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt'])
		const encryptionKey = uint8ToBase64(new Uint8Array(await window.crypto.subtle.exportKey('raw', key)))

		const bc = new BroadcastChannel(`wix-idp-${sessionId}`)

		bc.addEventListener('message', async (event) => {
			const { data: eventData } = event
			const { iv, data } = eventData
			const decryptedData = await window.crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, data)
			const loginResponse = JSON.parse(new TextDecoder().decode(decryptedData))
			if (loginResponse.error) {
				reject(loginResponse.error)
			} else {
				resolve(JSON.parse(loginResponse.response))
			}
			bc.postMessage(await encryptedCloseMessage(key))
			bc.close()
		})

		const finalUrl = buildUrlWithQueryParams(url, sessionId, encryptionKey)
		window.open(finalUrl, 'oauthPopup', 'width=450,height=522')
	})
}

const uint8ToBase64 = (arr: Uint8Array): string =>
	btoa(
		Array(arr.length)
			.fill('')
			.map((_, i) => String.fromCharCode(arr[i]))
			.join('')
	)

const encryptedCloseMessage = async (key: CryptoKey) => {
	const iv = window.crypto.getRandomValues(new Uint8Array(12))
	const data = await window.crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, new TextEncoder().encode('close'))
	return { iv, data }
}

const buildUrlWithQueryParams = (url: string, sessionId: string, encryptionKey: string) => {
	const queryParams = { sessionId, encryptionKey }
	const searchParams = convertToSearchParams(queryParams)

	try {
		const parsedUrl = new URL(url)
		return addParamsToUrl(parsedUrl, searchParams)
	} catch {
		return `${url}&${searchParams.toString()}`
	}
}

const convertToSearchParams = (params: Record<string, string>): URLSearchParams => {
	const searchParams = new URLSearchParams()

	for (const key in params) {
		if (params.hasOwnProperty(key)) {
			searchParams.append(key, params[key].toString())
		}
	}

	return searchParams
}

const addParamsToUrl = (url: URL, searchParams: URLSearchParams) => {
	const urlSearchParams = url.searchParams

	for (const [key, value] of searchParams) {
		urlSearchParams.append(key, value)
	}

	return url.toString()
}

export const getPrivacyStatus = (isJoinCommunityChecked: boolean): IPrivacyStatus => {
	return isJoinCommunityChecked ? 'PUBLIC' : 'PRIVATE'
}
